'use client';

import { useAnalytics } from '@repo/analytics/analytics-provider';
import { Button, IconButton } from '@repo/design-system/components/ds/button';
import { Icon } from '@repo/design-system/components/ds/icon';
import { DropdownMenu, DropdownMenuItem, DropdownMenuSeparator } from '@repo/design-system/components/ui/dropdown-menu';
import { toast } from '@repo/design-system/components/ui/toaster';
import { Type } from '@repo/design-system/components/ui/type';
import { useT } from '@repo/i18n/components/i18n.client';
import { useCurrentLanguage } from '@repo/i18n/hooks/use-current-language';
import { css } from '@repo/panda/css';
import type { MapsAnalyticsEvents, SessionShareTrigger } from '@repo/types/analytic-events/maps-analytic-events';
import type { GameSessionInternal } from '@repo/types/sessions/session-types';
import { uuidToBase62 } from '@repo/utility/base62';
import { buildDiscordShareText, buildRedditShareUrl, buildTwitterShareUrl } from '@repo/utility/social-share';
import { toggleSessionSharing } from '@repo/wemod-api-client/sessions-api-client';
import { useCallback, useEffect, useRef, useState } from 'react';
import { DiscordIcon, RedditIcon, XIcon } from '../utils/social-icons';
const DISCORD_OPEN_DELAY = 2500;
const WAND_WEBSITE_URL = process.env.NEXT_PUBLIC_WAND_WEBSITE_URL ?? '';
function buildShareUrl(language: string, sessionUuid: string): string {
  const langPrefix = language === 'en' ? '' : `/${language}`;
  return `${WAND_WEBSITE_URL}${langPrefix}/sessions/${uuidToBase62(sessionUuid)}`;
}
interface SessionShareMenuProps {
  session: GameSessionInternal;
  onSharingChange?: (sessionUuid: string, isShared: boolean) => void;
  iconOnly?: boolean;
  placement?: 'bottom-start' | 'bottom-end' | 'top-start' | 'top-end';
  disabled?: boolean;
  trigger: SessionShareTrigger;
}
export const SessionShareMenu = ({
  session,
  onSharingChange,
  iconOnly = true,
  placement = 'bottom-end',
  disabled = false,
  trigger
}: SessionShareMenuProps) => {
  const {
    t
  } = useT(['game_map']);
  const language = useCurrentLanguage();
  const analytics = useAnalytics();
  const [isShared, setIsShared] = useState(session.isShared);
  const [isToggling, setIsToggling] = useState(false);
  const discordTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    setIsShared(session.isShared);
  }, [session.isShared]);
  useEffect(() => {
    return () => {
      if (discordTimeoutRef.current) {
        clearTimeout(discordTimeoutRef.current);
      }
    };
  }, []);
  const shareUrl = buildShareUrl(language, session.uuid);
  const shareTitle = session.titleName ? t($ => $.session_history.share_title_with_game, {
    gameName: session.titleName
  }) : t($_0 => $_0.session_history.share_title_fallback);
  const enableSharing = useCallback(async () => {
    if (isToggling) return;
    setIsToggling(true);
    try {
      const response = await toggleSessionSharing(session.uuid, true, {
        titleId: session.titleId,
        startTime: session.startedAt,
        endTime: session.completedAt || new Date().toISOString()
      });
      setIsShared(response.is_shareable);
      onSharingChange?.(session.uuid, response.is_shareable);
      analytics?.event('session_share_enable', {
        sessionUuid: session.uuid,
        titleId: session.titleId,
        trigger
      });
      toast({
        description: t($_2 => $_2.session_history.share_enabled),
        variant: 'success'
      });
    } catch {
      toast({
        description: t($_1 => $_1.session_history.share_toggle_failed),
        variant: 'error'
      });
    } finally {
      setIsToggling(false);
    }
  }, [session, isToggling, onSharingChange, analytics, t, trigger]);
  const disableSharing = useCallback(async () => {
    if (isToggling) return;
    setIsToggling(true);
    try {
      const response_0 = await toggleSessionSharing(session.uuid, false, {
        titleId: session.titleId,
        startTime: session.startedAt,
        endTime: session.completedAt || new Date().toISOString()
      });
      setIsShared(response_0.is_shareable);
      onSharingChange?.(session.uuid, response_0.is_shareable);
      analytics?.event('session_share_disable', {
        sessionUuid: session.uuid,
        titleId: session.titleId,
        trigger
      });
      toast({
        description: t($_4 => $_4.session_history.share_disabled),
        variant: 'success'
      });
    } catch {
      toast({
        description: t($_3 => $_3.session_history.share_toggle_failed),
        variant: 'error'
      });
    } finally {
      setIsToggling(false);
    }
  }, [session, isToggling, onSharingChange, analytics, t, trigger]);
  const handleOpenChange = useCallback((details: {
    open: boolean;
  }) => {
    if (details.open && !isShared && !isToggling) {
      void enableSharing();
    }
  }, [isShared, isToggling, enableSharing]);
  const trackEvent = useCallback((eventName: keyof MapsAnalyticsEvents) => {
    analytics?.event(eventName, {
      sessionUuid: session.uuid,
      titleId: session.titleId,
      trigger
    });
  }, [analytics, session.uuid, session.titleId, trigger]);
  const handleCopyLink = useCallback(async () => {
    if (!isShared) return;
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast({
        description: t($_6 => $_6.session_history.share_link_copied),
        variant: 'success'
      });
    } catch {
      toast({
        description: t($_5 => $_5.session_history.copy_link_failed),
        variant: 'error'
      });
    }
    trackEvent('session_share_copy_link');
  }, [isShared, shareUrl, t, trackEvent]);
  const handlePostToReddit = useCallback(() => {
    if (!isShared) return;
    window.open(buildRedditShareUrl(shareUrl, shareTitle), '_blank');
    trackEvent('session_share_reddit');
  }, [isShared, shareUrl, shareTitle, trackEvent]);
  const handlePostToX = useCallback(() => {
    if (!isShared) return;
    window.open(buildTwitterShareUrl(shareUrl, shareTitle), '_blank');
    trackEvent('session_share_x');
  }, [isShared, shareUrl, shareTitle, trackEvent]);
  const handleShareOnDiscord = useCallback(async () => {
    if (!isShared) return;
    try {
      await navigator.clipboard.writeText(buildDiscordShareText(shareTitle, shareUrl));
      toast({
        description: t($_8 => $_8.session_history.discord_share_copied),
        variant: 'success'
      });
      discordTimeoutRef.current = setTimeout(() => {
        window.open('https://discord.com/channels/@me', '_blank');
        discordTimeoutRef.current = null;
      }, DISCORD_OPEN_DELAY);
      trackEvent('session_share_discord');
    } catch {
      toast({
        description: t($_7 => $_7.session_history.copy_link_failed),
        variant: 'error'
      });
    }
  }, [isShared, shareUrl, shareTitle, t, trackEvent]);
  const triggerButton = iconOnly ? <IconButton variant="ghost" color="secondary" size="sm" disabled={disabled || isToggling} aria-label={t($_9 => $_9.session_history.share_session)}>
            <Icon icon="ios_share" decorative />
        </IconButton> : <Button variant="filled" color="secondary" size="sm" disabled={disabled || isToggling} title={t($_10 => $_10.session_history.share_session)} className={css({
    minHeight: '[32px]'
  })}>
            <Icon icon="ios_share" decorative />
        </Button>;
  return <DropdownMenu trigger={triggerButton} placement={placement} variant="dark" size="md" closeOnSelect onOpenChange={handleOpenChange}>
            {isToggling && <div className={css({
      display: 'flex',
      alignItems: 'center',
      gap: '8',
      padding: '12'
    })}>
                    <Icon icon="progress_activity" size="sm" className={css({
        animation: 'spin',
        animationDuration: '[1s]',
        animationTimingFunction: 'linear',
        animationIterationCount: 'infinite'
      })} decorative />
                    <Type variant="body-s">
                        {isShared ? t($_11 => $_11.session_history.disabling_sharing) : t($_12 => $_12.session_history.enabling_sharing)}
                    </Type>
                </div>}

            {!isToggling && !isShared && <div className={css({
      padding: '12'
    })}>
                    <Type variant="body-s" color="text.secondary">
                        {t($_13 => $_13.session_history.share_enable_prompt)}
                    </Type>
                </div>}

            {!isToggling && isShared && <>
                    <DropdownMenuItem value="copy-link" leadingIcon="link" onSelect={handleCopyLink}>
                        {t($_14 => $_14.session_history.copy_link)}
                    </DropdownMenuItem>
                    <DropdownMenuItem value="reddit" onSelect={handlePostToReddit} asChild={false}>
                        <span className={css({
          display: 'flex',
          alignItems: 'center',
          gap: '12'
        })}>
                            <RedditIcon />
                            {t($_15 => $_15.session_history.post_to_reddit)}
                        </span>
                    </DropdownMenuItem>
                    <DropdownMenuItem value="x" onSelect={handlePostToX} asChild={false}>
                        <span className={css({
          display: 'flex',
          alignItems: 'center',
          gap: '12'
        })}>
                            <XIcon />
                            {t($_16 => $_16.session_history.post_to_x)}
                        </span>
                    </DropdownMenuItem>
                    <DropdownMenuItem value="discord" onSelect={handleShareOnDiscord} asChild={false}>
                        <span className={css({
          display: 'flex',
          alignItems: 'center',
          gap: '12'
        })}>
                            <DiscordIcon />
                            {t($_17 => $_17.session_history.share_on_discord)}
                        </span>
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem value="disable" leadingIcon="link_off" destructive onSelect={disableSharing}>
                        {t($_18 => $_18.session_history.disable_link)}
                    </DropdownMenuItem>
                </>}
        </DropdownMenu>;
};